import {
  Alert, Box, Button, Card, Divider, Grid, Group, List, NumberInput,
  Progress, SegmentedControl, Select, SimpleGrid, Stack, Text, Textarea,
  TextInput, Title
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { DisabledReason, HelpButtonGroup, HelpIcon, helpText, i18n, nextStepToast, showNotification, useHelpContext } from "@dehub/theme";
import {
  ArrowLeftIcon, CheckCircleIcon, ClockIcon, PackageIcon, UserIcon, WarningIcon
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import useSWR from "swr";
import { DeliveryApi, ReturnApi, TenantReturnsSettingsApi } from "../../../apis";
import type {
  IConfirmWarehouseReceiptItem, InventoryImpactType, IReturnItem,
  ReceivedCondition
} from "../../../apis/return";
import { DotPill } from "../../../components/delivery/atoms";
import { translateBackendError } from "../../../components/delivery/translate-error";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import { reasonLabel } from "../../../components/return/atoms/reason-badge";
import { ReturnStatusBadge } from "../../../components/return/atoms";
import { ListState } from "../../../components/ui";
import { IAuth } from "../../../store/Auth";
import { PhotoUploader } from "./PhotoUploader";
import { currencyFormat } from "../../../utils/currency";

// §D10 — buckets that put units back on a shelf require a storage location and
// move stock; the other two are audit-only.
/** @dehub/help key namespace of this screen (spec 12 §0). */
const HELP = "order.returns-warehouse-receipt";

const STOCK_BUCKETS: InventoryImpactType[] = ["RESTOCK_GOOD", "DAMAGED_SEPARATE_STOCK"];

type TFn = (k: string, o?: Record<string, unknown>) => string;

const conditionOptions = (t: TFn): { value: ReceivedCondition; label: string }[] => [
  { value: "EXPECTED",             label: t("warehouseReceipt.condition.EXPECTED") },
  { value: "WORSE_THAN_EXPECTED",  label: t("warehouseReceipt.condition.WORSE_THAN_EXPECTED") },
  { value: "BETTER_THAN_EXPECTED", label: t("warehouseReceipt.condition.BETTER_THAN_EXPECTED") }
];

const bucketOptions = (t: TFn): { value: InventoryImpactType; name: string; desc: string }[] => [
  { value: "RESTOCK_GOOD",           name: t("warehouseReceipt.bucket.RESTOCK_GOOD"),           desc: t("warehouseReceipt.bucket.RESTOCK_GOOD_desc") },
  { value: "DAMAGED_SEPARATE_STOCK", name: t("warehouseReceipt.bucket.DAMAGED_SEPARATE_STOCK"), desc: t("warehouseReceipt.bucket.DAMAGED_SEPARATE_STOCK_desc") },
  { value: "DAMAGED_WRITE_OFF",      name: t("warehouseReceipt.bucket.DAMAGED_WRITE_OFF"),      desc: t("warehouseReceipt.bucket.DAMAGED_WRITE_OFF_desc") },
  { value: "EXPIRED_DESTROY",        name: t("warehouseReceipt.bucket.EXPIRED_DESTROY"),        desc: t("warehouseReceipt.bucket.EXPIRED_DESTROY_desc") }
];

interface ItemForm {
  actualQuantity: number;
  receivedCondition: ReceivedCondition;
  inventoryImpact: InventoryImpactType;
  bucketLocationCode: string;
  photoUrls: string[];
  notes: string;
}

function defaultForm(item: IReturnItem): ItemForm {
  return {
    actualQuantity   : item.quantity,
    receivedCondition: "EXPECTED",
    inventoryImpact  : "RESTOCK_GOOD",
    bucketLocationCode: "",
    photoUrls        : [],
    notes            : ""
  };
}

// One validated error string per item, or null when the line is complete.
function validateItem(form: ItemForm, accepted: number): string | null {
  if (!Number.isInteger(form.actualQuantity) || form.actualQuantity < 1) {
    return i18n.t("returns:warehouseReceipt.err.qtyMin");
  }
  if (form.actualQuantity > accepted) {
    return i18n.t("returns:warehouseReceipt.err.qtyMax", { accepted });
  }
  if (STOCK_BUCKETS.includes(form.inventoryImpact) && !form.bucketLocationCode.trim()) {
    return i18n.t("returns:warehouseReceipt.err.locationRequired");
  }
  if (form.receivedCondition === "WORSE_THAN_EXPECTED" && form.photoUrls.length < 1) {
    return i18n.t("returns:warehouseReceipt.err.photoRequired");
  }
  return null;
}

export function WarehouseReceiptPage() {
  const { t } = useTranslation("returns");
  const CONDITION_OPTIONS = conditionOptions(t);
  const BUCKET_OPTIONS = bucketOptions(t);
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: { auth: IAuth }) => state.auth.user);
  const businessId = user?.currentBusinessId ?? null;
  const RECEIVE = usePermission("RETURN_WAREHOUSE_RECEIVE");

  const { data, isLoading, error, mutate } = useSWR(
    id ? ["returns.get", id] : null,
    () => ReturnApi.get(id),
    { revalidateOnFocus: false }
  );

  const ret = data?.returnRequest;

  // @dehub/help: the return status picks the drawer's status row; the page itself has one step («Одоо» card).
  const { set: setHelpContext } = useHelpContext();
  useEffect(() => {
    setHelpContext({ status: ret?.status ?? undefined });
  }, [ret?.status, setHelpContext]);
  const items = data?.items ?? [];
  const isGateState = ret?.status === "AWAITING_WAREHOUSE_RECEIPT";

  // The return already names the warehouse that must receive it (GET returns targetWarehouseId/Name,
  // O174). A different or non-responsible warehouse is refused by the backend with RET-044, so when
  // the target is known it is the only choice; otherwise the business warehouses are listed.
  const { data: warehouseRows = [] } = useSWR(
    businessId && ret && !ret.targetWarehouseId ? ["delivery.warehouse-select", businessId] : null,
    () => DeliveryApi.warehouseSelect({ businessId: businessId as string, limit: 100 }),
    { revalidateOnFocus: false }
  );
  const warehouseOptions = ret?.targetWarehouseId
    ? [{ value: ret.targetWarehouseId, label: ret.targetWarehouseName ?? t("warehouseReceipt.targetWarehouse") }]
    : (warehouseRows as Array<{ id: string; name: string }>).map((w) => ({ value: w.id, label: w.name }));

  // Tenant SLA window (D10 R-106); silently absent when the user may not read the settings.
  const { data: tenantSettings } = useSWR(
    isGateState ? ["tenant-returns-settings"] : null,
    () => TenantReturnsSettingsApi.get(),
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );

  const [warehouseId, setWarehouseId] = useState<string | null>(null);
  const [receivedAt, setReceivedAt] = useState<string | null>(null);
  const [receiverNotes, setReceiverNotes] = useState("");
  const [forms, setForms] = useState<Record<string, ItemForm>>({});
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);

  // Initial value = the return's target warehouse; auto-pick the only warehouse of a business with one.
  const resolvedWarehouseId = warehouseId
    ?? ret?.targetWarehouseId
    ?? (warehouseOptions.length === 1 ? warehouseOptions[0].value : null);

  const getForm = (item: IReturnItem): ItemForm => forms[item.id] ?? defaultForm(item);
  const updateForm = (itemId: string, patch: Partial<ItemForm>) =>
    setForms((prev) => {
      const base = prev[itemId] ?? defaultForm(items.find((i) => i.id === itemId)!);
      return { ...prev, [itemId]: { ...base, ...patch } };
    });

  // ── SLA: from awaitingWarehouseReceiptSince and the tenant's warehouseReceiptSlaDays (O175) ──
  const sla = useMemo(() => {
    if (!ret?.awaitingWarehouseReceiptSince) return null;
    const start = dayjs(ret.awaitingWarehouseReceiptSince);
    const days = tenantSettings?.warehouseReceiptSlaDays
      ?? (ret.slaDeadline ? Math.round(dayjs(ret.slaDeadline).diff(start, "day", true)) : null);
    if (!days) return null;
    const deadline = start.add(days, "day");
    const remaining = ret.slaDaysRemaining ?? deadline.diff(dayjs(), "day");
    const elapsed = Math.min(days, Math.max(0, dayjs().diff(start, "day")));
    return {
      start, deadline, remaining, days,
      percent: Math.min(100, Math.round((elapsed / days) * 100)),
      overdue: remaining < 0
    };
  }, [ret?.awaitingWarehouseReceiptSince, ret?.slaDeadline, ret?.slaDaysRemaining, tenantSettings?.warehouseReceiptSlaDays]);

  const itemErrors = useMemo(() => {
    const map: Record<string, string | null> = {};
    for (const it of items) map[it.id] = validateItem(getForm(it), it.quantity);
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forms, items]);

  const hasItemErrors = Object.values(itemErrors).some(Boolean);

  const handleSubmit = async () => {
    setShowErrors(true);
    if (!resolvedWarehouseId) {
      showNotification({ color: "yellow", title: t("toast.error"), message: helpText(`${HELP}.validation.warehouse`) ?? t("warehouseReceipt.selectWarehouse") });
      return;
    }
    if (hasItemErrors) {
      showNotification({ color: "yellow", title: t("toast.error"), message: helpText(`${HELP}.validation.items`) ?? t("warehouseReceipt.fillEachItem") });
      return;
    }
    const payloadItems: IConfirmWarehouseReceiptItem[] = items.map((it) => {
      const f = getForm(it);
      return {
        returnItemId      : it.id,
        actualQuantity    : f.actualQuantity,
        inventoryImpact   : f.inventoryImpact,
        bucketLocationCode: f.bucketLocationCode.trim() || null,
        receivedCondition : f.receivedCondition,
        photoUrls         : f.photoUrls.length ? f.photoUrls : undefined,
        notes             : f.notes.trim() || null
      };
    });
    try {
      setBusy(true);
      const res = await ReturnApi.confirmWarehouseReceipt(id, {
        warehouseId    : resolvedWarehouseId,
        receivedAt     : receivedAt ? dayjs(receivedAt).toISOString() : undefined,
        receiverNotes  : receiverNotes.trim() || null,
        expectedVersion: ret?.version,
        items          : payloadItems
      });
      // The receipt is recorded either way, but the units may not have reached
      // the shelf. Saying "Амжилт" in that case is how a receipt that moved no
      // stock went unnoticed: name it and point at where it is tracked.
      const issues = res?.stockIssues ?? [];
      if (issues.length > 0) {
        showNotification({
          color  : "yellow",
          title  : t("warehouseReceipt.stockIssueTitle"),
          message: t("warehouseReceipt.stockIssueMessage", { count: issues.length }),
          autoClose: false
        });
      } else {
        nextStepToast(`${HELP}.toast.receive`, { fallback: { title: t("toast.success"), message: t("warehouseReceipt.confirmed") } });
      }
      navigate(`/returns/${id}`);
    } catch (e) {
      showNotification({ color: "red", title: t("toast.error"), message: translateBackendError(e) });
    } finally {
      setBusy(false);
    }
  };

  const breadcrumb = [
    { to: "/", label: t("breadcrumb.home") },
    { to: "/returns", label: t("breadcrumb.returns") },
    { to: `/returns/${id}`, label: ret?.returnNumber ?? "-" },
    { label: t("warehouseReceipt.breadcrumb") }
  ];

  const remainingColor = !sla ? undefined : sla.overdue ? "red.8" : "dark.7";

  return (
    <PageLayout
      title={t("warehouseReceipt.title")}
      subTitle={ret ? `${ret.returnNumber} · ${ret.buyerBusinessName ?? "-"}` : undefined}
      breadcrumb={breadcrumb}
      loading={isLoading && !data}
      extra={ret ? [<ReturnStatusBadge key="status" status={ret.status} size="lg" />] : undefined}
    >
      {error ? (
        <ListState state="error" title={t("toast.error")} description={t("warehouseReceipt.loadError")} onRetry={() => mutate()} />
      ) : null}

      {data && ret ? (
        !isGateState ? (
          <Alert color="yellow" variant="light" icon={<WarningIcon size={18} />} title={t("warehouseReceipt.cannotReceiveTitle")}>
            <Text fz={14}>
              {t("warehouseReceipt.cannotReceiveLead")} <ReturnStatusBadge status={ret.status} size="sm" /> {t("warehouseReceipt.cannotReceiveTail1")}{" "}
              <Text span fz={14} fw={600}>{t("status.AWAITING_WAREHOUSE_RECEIPT")}</Text> {t("warehouseReceipt.cannotReceiveTail2")}
            </Text>
            <Box mt="sm">
              <Button variant="default" leftSection={<ArrowLeftIcon size={16} />} onClick={() => navigate(`/returns/${id}`)}>
                {t("warehouseReceipt.backToDetail")}
              </Button>
            </Box>
          </Alert>
        ) : (
          <Stack gap="md">
            <Alert color="yellow" variant="light" icon={<WarningIcon size={18} />}>
              <Text fz={14}>
                <Text span fz={14} fw={700}>{t("warehouseReceipt.gatewayWarnBold")}</Text> {t("warehouseReceipt.gatewayWarnMid")}
              </Text>
            </Alert>

            <Grid>
              {/* ── Left: receipt form ── */}
              <Grid.Col span={{ base: 12, md: 8 }}>
                <Stack gap="md">
                  <Card withBorder shadow="sm" radius="md">
                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                      <Select
                        label={t("resolutionPanel.warehouse")}
                        placeholder={t("warehouseReceipt.warehousePlaceholder")}
                        description={ret.targetWarehouseId ? t("warehouseReceipt.targetWarehouseHint") : undefined}
                        data={warehouseOptions}
                        value={resolvedWarehouseId}
                        onChange={setWarehouseId}
                        error={showErrors && !resolvedWarehouseId ? helpText(`${HELP}.validation.warehouse`) ?? t("warehouseReceipt.selectWarehouse") : undefined}
                        required
                        searchable
                      />
                      <DateTimePicker
                        label={t("warehouseReceipt.receivedAt")}
                        placeholder={t("warehouseReceipt.receivedAtPlaceholder")}
                        value={receivedAt}
                        onChange={setReceivedAt}
                        clearable
                      />
                    </SimpleGrid>
                  </Card>

                  {items.map((item, idx) => {
                    const f = getForm(item);
                    const err = itemErrors[item.id];
                    const isShort = f.actualQuantity < item.quantity;
                    const needsLocation = STOCK_BUCKETS.includes(f.inventoryImpact);
                    const needsPhoto = f.receivedCondition === "WORSE_THAN_EXPECTED";
                    return (
                      <Card key={item.id} withBorder shadow="sm" radius="md">
                        <Group gap="sm" mb="md" wrap="nowrap">
                          <Box c="gray.8" bg="gray.1" p="sm" style={{ borderRadius: "var(--mantine-radius-md)", display: "flex" }}>
                            <PackageIcon size={24} />
                          </Box>
                          <Box style={{ flex: 1 }}>
                            <Text fz={16} fw={600}>{item.productName}{item.variantName && item.variantName !== item.productName ? ` · ${item.variantName}` : ""}</Text>
                            <Text fz={14} c="gray.8">SKU: {item.sku ?? "-"}</Text>
                            <Group gap="md" mt={4}>
                              <Text fz={14}><Text span fz={14} c="gray.8">{t("warehouseReceipt.acceptedLabel")}</Text> <Text span fz={14} fw={600}>{t("warehouseReceipt.pieces", { count: item.quantity })}</Text></Text>
                              <Text fz={14}><Text span fz={14} c="gray.8">{t("warehouseReceipt.reasonLabel")}</Text> <Text span fz={14} fw={600}>{reasonLabel(item.returnReason) || "-"}</Text></Text>
                              <Text fz={14}><Text span fz={14} c="gray.8">{t("warehouseReceipt.amountLabel")}</Text> <Text span fz={14} fw={600}>{ret.currency === "MNT" ? currencyFormat(item.lineTotal) : `${item.lineTotal.toLocaleString("mn-MN")} ${ret.currency}`}</Text></Text>
                            </Group>
                          </Box>
                          <Text fz={14} fw={600} c="gray.8">#{idx + 1}</Text>
                        </Group>

                        <Divider mb="md" />

                        <Stack gap="md">
                          {/* 1 — physical quantity */}
                          <Box>
                            <Text fz={14} fw={600} mb={6}>{t("warehouseReceipt.step1")}<HelpIcon helpKey={`${HELP}.field.qty`} /></Text>
                            <Group align="flex-end" gap="sm">
                              <NumberInput
                                value={f.actualQuantity}
                                onChange={(v) => updateForm(item.id, { actualQuantity: typeof v === "number" ? v : 0 })}
                                min={1}
                                max={item.quantity}
                                step={1}
                                allowDecimal={false}
                                w={140}
                                aria-label={t("warehouseReceipt.step1")}
                              />
                              <Text fz={14} c="gray.8" pb={6}>{t("warehouseReceipt.expectedSuffix", { count: item.quantity })}</Text>
                            </Group>
                            {isShort ? (
                              <Text fz={14} fw={600} mt={4}>
                                {t("warehouseReceipt.shortReceived")}
                              </Text>
                            ) : null}
                          </Box>

                          {/* 2 — physical condition */}
                          <Box>
                            <Text fz={14} fw={600} mb={6}>{t("warehouseReceipt.step2")}<HelpIcon helpKey={`${HELP}.field.condition`} /></Text>
                            <SegmentedControl
                              fullWidth
                              data={CONDITION_OPTIONS}
                              value={f.receivedCondition}
                              onChange={(v) => updateForm(item.id, { receivedCondition: v as ReceivedCondition })}
                            />
                          </Box>

                          {/* 3 — inventory bucket */}
                          <Box>
                            <Text fz={14} fw={600} mb={6}>{t("warehouseReceipt.step3")}<HelpIcon helpKey={`${HELP}.types.bucket`} /></Text>
                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                              {BUCKET_OPTIONS.map((b) => {
                                const active = f.inventoryImpact === b.value;
                                return (
                                  <Card
                                    key={b.value}
                                    withBorder
                                    radius="md"
                                    padding="sm"
                                    component="button"
                                    type="button"
                                    aria-pressed={active}
                                    onClick={() => updateForm(item.id, { inventoryImpact: b.value })}
                                    styles={{
                                      root: {
                                        cursor: "pointer",
                                        textAlign: "left",
                                        borderColor: active ? "var(--mantine-color-dark-7)" : undefined,
                                        borderWidth: active ? 2 : undefined,
                                        background: active ? "var(--mantine-color-gray-0)" : undefined
                                      }
                                    }}
                                  >
                                    <Text fz={14} fw={600}>{b.name}</Text>
                                    <Text fz={14} c="gray.8">{b.desc}</Text>
                                  </Card>
                                );
                              })}
                            </SimpleGrid>
                          </Box>

                          {/* 4 — storage location */}
                          <TextInput
                            label={<>{t("warehouseReceipt.step4")}<HelpIcon helpKey={`${HELP}.field.location`} /></>}
                            description={needsLocation ? t("warehouseReceipt.locationRequiredDesc") : t("warehouseReceipt.optional")}
                            placeholder={t("warehouseReceipt.locationPlaceholder")}
                            value={f.bucketLocationCode}
                            onChange={(e) => updateForm(item.id, { bucketLocationCode: e.currentTarget.value })}
                            maxLength={64}
                            required={needsLocation}
                            error={showErrors && needsLocation && !f.bucketLocationCode.trim()
                              ? helpText(`${HELP}.validation.location`) ?? t("warehouseReceipt.locationRequiredShort") : undefined}
                          />

                          {/* 5 — photo evidence */}
                          <Box>
                            <Text fz={14} fw={600} mb={6}>
                              {t("warehouseReceipt.step5")}<HelpIcon helpKey={`${HELP}.field.photo`} /> <Text span fz={14} fw={400} c="gray.8">{needsPhoto ? t("warehouseReceipt.photoRequiredHint") : t("warehouseReceipt.photoOptionalHint")}</Text>
                            </Text>
                            <PhotoUploader
                              value={f.photoUrls}
                              onChange={(urls) => updateForm(item.id, { photoUrls: urls })}
                              error={showErrors && needsPhoto && f.photoUrls.length < 1
                                ? helpText(`${HELP}.validation.photo`) ?? t("warehouseReceipt.photoRequiredShort") : null}
                            />
                          </Box>

                          {/* 6 — item notes */}
                          <Textarea
                            label={t("warehouseReceipt.step6")}
                            placeholder={t("warehouseReceipt.notesPlaceholder")}
                            value={f.notes}
                            onChange={(e) => updateForm(item.id, { notes: e.currentTarget.value })}
                            autosize
                            minRows={2}
                          />

                          {showErrors && err ? (
                            <Alert color="yellow" variant="light" icon={<WarningIcon size={16} />} p="xs">
                              <Text fz={14}>{err}</Text>
                            </Alert>
                          ) : null}
                        </Stack>
                      </Card>
                    );
                  })}

                  <Card withBorder shadow="sm" radius="md">
                    <Textarea
                      label={t("warehouseReceipt.receiverNotesLabel")}
                      placeholder={t("warehouseReceipt.receiverNotesPlaceholder")}
                      value={receiverNotes}
                      onChange={(e) => setReceiverNotes(e.currentTarget.value)}
                      autosize
                      minRows={2}
                    />
                    <Group justify="space-between" align="center" mt="md">
                      <Text fz={14} maw={420}>
                        <Text span fz={14} fw={700}>{t("warehouseReceipt.warnPrefix")}</Text> {t("warehouseReceipt.warnBody")}
                      </Text>
                      <Group gap="sm">
                        <Button variant="default" onClick={() => navigate(`/returns/${id}`)} disabled={busy}>
                          {t("resolutionPanel.cancel")}
                        </Button>
                        {/* @dehub/help: attached «?» (R2); without the permission the reason popover (R4). */}
                        <DisabledReason helpKey={`${HELP}.reason.receive`} reason="PERM" when={!RECEIVE.hasAccess} position="bottom-end">
                          <HelpButtonGroup helpKey={`${HELP}.action.receive`}>
                            <Button
                              data-help-id="return-receive-submit"
                              size="md"
                              leftSection={<CheckCircleIcon size={18} />}
                              onClick={handleSubmit}
                              loading={busy}
                            >
                              {t("warehouseReceipt.submitCta")}
                            </Button>
                          </HelpButtonGroup>
                        </DisabledReason>
                      </Group>
                    </Group>
                  </Card>
                </Stack>
              </Grid.Col>

              {/* ── Right: context ── */}
              <Grid.Col span={{ base: 12, md: 4 }}>
                <Stack gap="md">
                  <Card withBorder shadow="sm" radius="md">
                    <Group gap="xs" mb="sm">
                      <UserIcon size={18} />
                      <Title order={5}>{t("warehouseReceipt.responsible")}<HelpIcon helpKey={`${HELP}.section.responsible`} variant="section" /></Title>
                    </Group>
                    <Text fz={14} fw={600} mb={4}>{t("warehouseReceipt.keeper")}</Text>
                    <DotPill tone="amber">{t("warehouseReceipt.keeper")}</DotPill>
                    <Text fz={14} mt="sm">
                      {t("warehouseReceipt.criteriaText")} {t("warehouseReceipt.criteriaTail")}
                    </Text>
                    {!RECEIVE.hasAccess && (
                      <Text fz={14} fw={600} mt="xs">{RECEIVE.reason}</Text>
                    )}
                  </Card>

                  <Card withBorder shadow="sm" radius="md">
                    <Group gap="xs" mb="sm">
                      <ClockIcon size={18} />
                      <Title order={5}>{t("warehouseReceipt.slaMonitor")}<HelpIcon helpKey={`${HELP}.section.sla`} variant="section" /></Title>
                    </Group>
                    {sla ? (
                      <Stack gap={6}>
                        <Group justify="space-between">
                          <Text fz={14} c="gray.8">{t("warehouseReceipt.gateEntered")}</Text>
                          <Text fz={14} fw={500}>{sla.start.format("YYYY-MM-DD HH:mm")}</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text fz={14} c="gray.8">{t("warehouseReceipt.slaLimit")}</Text>
                          <Text fz={14} fw={500}>{t("warehouseReceipt.daysWithDeadline", { count: sla.days, date: sla.deadline.format("YYYY-MM-DD") })}</Text>
                        </Group>
                        <Group justify="space-between">
                          <Text fz={14} c="gray.8">{t("warehouseReceipt.remaining")}</Text>
                          <Text fz={14} fw={700} c={remainingColor}>
                            {sla.overdue ? t("warehouseReceipt.daysOverdue", { count: Math.abs(sla.remaining) }) : t("warehouseReceipt.daysValue", { count: sla.remaining })}
                          </Text>
                        </Group>
                        <Progress value={sla.percent} color="dark.7" size="sm" mt={4} aria-label={t("warehouseReceipt.slaMonitor")} />
                      </Stack>
                    ) : (
                      <Text fz={14}>
                        {t("warehouseReceipt.slaUnknown")}
                      </Text>
                    )}
                  </Card>

                  <Card withBorder shadow="sm" radius="md">
                    <Group gap="xs" mb="sm">
                      <CheckCircleIcon size={18} />
                      <Title order={5}>{t("warehouseReceipt.autoAfterTitle")}<HelpIcon helpKey={`${HELP}.section.auto-after`} variant="section" /></Title>
                    </Group>
                    <List size="sm" spacing={4}>
                      <List.Item>{t("warehouseReceipt.auto1")}</List.Item>
                      <List.Item>{t("warehouseReceipt.auto2")}</List.Item>
                      <List.Item>{t("warehouseReceipt.auto3")}</List.Item>
                      <List.Item>{t("warehouseReceipt.auto4")}</List.Item>
                      <List.Item>{t("warehouseReceipt.auto5")}</List.Item>
                    </List>
                  </Card>
                </Stack>
              </Grid.Col>
            </Grid>
          </Stack>
        )
      ) : null}
    </PageLayout>
  );
}
